import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  ChevronRight, ChevronLeft, Check, AlertCircle, Loader2, Save,
  Upload, X, FileText, Calendar, MapPin, Briefcase, User, Phone,
  Mail, MapPinned, DollarSign, Heart, Landmark, FileCheck
} from 'lucide-react';
import { backendApi } from './api/backendApi';
import toast from 'react-hot-toast';

// Design tokens
const colors = {
  primary: '#667eea',
  primaryDark: '#764ba2',
  success: '#4CAF50',
  warning: '#ff9800',
  error: '#f44336',
  border: '#e0e0e0',
  bg: '#f5f5f5',
  text: '#333',
  textLight: '#666',
  cardBg: '#fff',
};

const STEPS = [
  { id: 1, title: 'Personal Info', icon: User },
  { id: 2, title: 'Address', icon: MapPin },
  { id: 3, title: 'Employment', icon: Briefcase },
  { id: 4, title: 'Documents', icon: FileCheck },
  { id: 5, title: 'Banking & Emergency', icon: Landmark },
];

const DOCUMENT_TYPES = [
  { value: 'id_proof', label: 'ID Proof (Aadhar/PAN/Passport)' },
  { value: 'offer_letter', label: 'Offer Letter' },
  { value: 'address_proof', label: 'Address Proof (Utility Bill/Lease)' },
  { value: 'resume', label: 'Resume/CV' },
  { value: 'qualification', label: 'Educational Qualification' },
  { value: 'other', label: 'Other Documents' },
];

const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Temporary'];

export default function OnboardingForm() {
  const { token } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingStatus, setSavingStatus] = useState('');
  const autoSaveTimerRef = useRef(null);

  // Form state
  const [inviteDetails, setInviteDetails] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [formData, setFormData] = useState({
    // Step 1: Personal
    fullName: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    nationality: '',
    
    // Step 2: Address
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'India',
    
    // Step 3: Employment
    employmentType: 'Full-time',
    salaryAmount: '',
    salaryCurrency: 'INR',
    
    // Step 4: Documents
    documents: [],
    
    // Step 5: Banking & Emergency
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelation: '',
    bankAccountName: '',
    bankAccountNumber: '',
    bankIfscCode: '',
    bankBranchName: '',
  });

  // Load invite and submission data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Validate invite
        const inviteRes = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/onboarding/invite/${token}`
        );
        if (!inviteRes.ok) throw new Error('Invalid or expired invite link');
        
        const inviteData = await inviteRes.json();
        setInviteDetails(inviteData.details);
        
        // Get or create submission
        const submissionRes = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/onboarding/submission/${token}`
        );
        const submissionData = await submissionRes.json();
        setSubmissionId(submissionData.submissionId);
        
        // Pre-fill email
        setFormData(prev => ({
          ...prev,
          email: inviteData.details.email
        }));
        
      } catch (error) {
        toast.error(error.message || 'Failed to load onboarding form');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [token]);

  // Auto-save timer
  useEffect(() => {
    if (!submissionId) return;
    
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Set new timer
    autoSaveTimerRef.current = setTimeout(() => {
      saveDraft();
    }, 30000); // Auto-save every 30 seconds
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [formData, submissionId]);

  const saveDraft = async () => {
    if (!submissionId) return;
    
    try {
      setSaving(true);
      setSavingStatus('Saving...');
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/onboarding/save-draft/${token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        }
      );
      
      if (!response.ok) throw new Error('Failed to save draft');
      
      setSavingStatus('✓ Saved');
      setTimeout(() => setSavingStatus(''), 2000);
    } catch (error) {
      setSavingStatus('✗ Save failed');
      console.error('Auto-save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = async (e, documentType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    try {
      setSaving(true);
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/onboarding/upload/${token}/${documentType}`,
        {
          method: 'POST',
          body: formDataToUpload
        }
      );
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      setFormData(prev => ({
        ...prev,
        documents: [...prev.documents, data.document]
      }));
      
      toast.success('Document uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload document');
      console.error('Upload error:', error);
    } finally {
      setSaving(false);
    }
  };

  const removeDocument = (documentId) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter(d => d.id !== documentId)
    }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.fullName || !formData.phone || !formData.dateOfBirth) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!formData.addressLine1 || !formData.city || !formData.state) {
      toast.error('Please fill in your address');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/onboarding/submit/${token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        }
      );
      
      if (!response.ok) throw new Error('Submission failed');
      
      // Success screen
      setCurrentStep(6);
      toast.success('Form submitted successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader2 size={48} style={{ color: colors.primary, animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        input, textarea, select {
          transition: border-color 0.2s;
        }
        input:focus, textarea:focus, select:focus {
          outline: none;
          border-color: ${colors.primary} !important;
          box-shadow: 0 0 0 3px ${colors.primary}20;
        }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.title}>Complete Your Onboarding</h1>
          <p style={styles.subtitle}>Welcome to our team! Let's get you set up.</p>
        </div>
      </div>

      <div style={styles.mainContainer}>
        {currentStep === 6 ? (
          // Success Screen
          <div style={styles.successCard}>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.successTitle}>Submission Complete!</h2>
            <p style={styles.successText}>
              Thank you for submitting your onboarding form. Our HR team will review your information and contact you within 1-2 business days.
            </p>
            <p style={styles.successSubtext}>
              Check your email for further updates. You'll receive your login credentials once your submission is approved.
            </p>
          </div>
        ) : (
          <>
            {/* Step Indicator */}
            <div style={styles.stepsContainer}>
              {STEPS.map((step, idx) => (
                <div key={step.id} style={styles.stepWrapper}>
                  <div
                    style={{
                      ...styles.stepDot,
                      backgroundColor: currentStep >= step.id ? colors.primary : colors.border,
                      color: currentStep >= step.id ? '#fff' : colors.textLight,
                    }}
                  >
                    {currentStep > step.id ? <Check size={20} /> : step.id}
                  </div>
                  <div style={styles.stepLabel}>{step.title}</div>
                  {idx < STEPS.length - 1 && (
                    <div
                      style={{
                        ...styles.stepLine,
                        backgroundColor: currentStep > step.id ? colors.primary : colors.border,
                      }}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Form Card */}
            <div style={styles.card}>
              {/* Current Step Content */}
              {currentStep === 1 && <Step1 formData={formData} onChange={handleInputChange} />}
              {currentStep === 2 && <Step2 formData={formData} onChange={handleInputChange} />}
              {currentStep === 3 && <Step3 formData={formData} onChange={handleInputChange} />}
              {currentStep === 4 && (
                <Step4
                  formData={formData}
                  onFileUpload={handleFileUpload}
                  onRemoveDocument={removeDocument}
                  saving={saving}
                />
              )}
              {currentStep === 5 && <Step5 formData={formData} onChange={handleInputChange} />}

              {/* Navigation and Save Status */}
              <div style={styles.formFooter}>
                <div style={styles.saveStatus}>
                  {savingStatus && (
                    <span style={{ color: savingStatus.includes('✓') ? colors.success : colors.textLight, fontSize: '13px' }}>
                      {savingStatus}
                    </span>
                  )}
                </div>

                <div style={styles.navigationButtons}>
                  <button
                    onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                    disabled={currentStep === 1}
                    style={{
                      ...styles.button,
                      ...styles.buttonSecondary,
                      opacity: currentStep === 1 ? 0.5 : 1,
                    }}
                  >
                    <ChevronLeft size={18} /> Previous
                  </button>

                  {currentStep < STEPS.length ? (
                    <button
                      onClick={() => {
                        saveDraft();
                        setCurrentStep(currentStep + 1);
                      }}
                      style={styles.button}
                    >
                      Next <ChevronRight size={18} />
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={submitting}
                      style={{
                        ...styles.button,
                        opacity: submitting ? 0.7 : 1,
                        cursor: submitting ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Submitting...
                        </>
                      ) : (
                        <>
                          <Check size={18} /> Submit Form
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Note */}
            <div style={styles.footerNote}>
              <AlertCircle size={16} />
              <span>Your progress is automatically saved every 30 seconds</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Step Components
// ============================================================================

function Step1({ formData, onChange }) {
  return (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>Personal Information</h2>

      <div style={styles.fieldGroup}>
        <label style={styles.label}>Full Name *</label>
        <input
          type="text"
          name="fullName"
          value={formData.fullName}
          onChange={onChange}
          placeholder="Your full name"
          style={styles.input}
          required
        />
      </div>

      <div style={styles.fieldRow}>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>Email *</label>
          <input
            type="email"
            value={formData.email}
            disabled
            style={{ ...styles.input, backgroundColor: '#f5f5f5' }}
          />
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>Phone *</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={onChange}
            placeholder="+91 9876543210"
            style={styles.input}
            required
          />
        </div>
      </div>

      <div style={styles.fieldRow}>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>Date of Birth *</label>
          <input
            type="date"
            name="dateOfBirth"
            value={formData.dateOfBirth}
            onChange={onChange}
            style={styles.input}
            required
          />
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>Gender</label>
          <select
            name="gender"
            value={formData.gender}
            onChange={onChange}
            style={styles.input}
          >
            <option value="">Select</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
            <option value="Prefer not to say">Prefer not to say</option>
          </select>
        </div>
      </div>

      <div style={styles.fieldGroup}>
        <label style={styles.label}>Nationality</label>
        <input
          type="text"
          name="nationality"
          value={formData.nationality}
          onChange={onChange}
          placeholder="Indian"
          style={styles.input}
        />
      </div>
    </div>
  );
}

function Step2({ formData, onChange }) {
  return (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>Address Information</h2>

      <div style={styles.fieldGroup}>
        <label style={styles.label}>Address Line 1 *</label>
        <input
          type="text"
          name="addressLine1"
          value={formData.addressLine1}
          onChange={onChange}
          placeholder="Street address"
          style={styles.input}
          required
        />
      </div>

      <div style={styles.fieldGroup}>
        <label style={styles.label}>Address Line 2</label>
        <input
          type="text"
          name="addressLine2"
          value={formData.addressLine2}
          onChange={onChange}
          placeholder="Apartment, building, etc."
          style={styles.input}
        />
      </div>

      <div style={styles.fieldRow}>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>City *</label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={onChange}
            placeholder="City"
            style={styles.input}
            required
          />
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>State *</label>
          <input
            type="text"
            name="state"
            value={formData.state}
            onChange={onChange}
            placeholder="State"
            style={styles.input}
            required
          />
        </div>
      </div>

      <div style={styles.fieldRow}>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>Postal Code</label>
          <input
            type="text"
            name="postalCode"
            value={formData.postalCode}
            onChange={onChange}
            placeholder="123456"
            style={styles.input}
          />
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>Country *</label>
          <input
            type="text"
            name="country"
            value={formData.country}
            onChange={onChange}
            style={styles.input}
            required
          />
        </div>
      </div>
    </div>
  );
}

function Step3({ formData, onChange }) {
  return (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>Employment Details</h2>

      <div style={styles.fieldGroup}>
        <label style={styles.label}>Employment Type</label>
        <select
          name="employmentType"
          value={formData.employmentType}
          onChange={onChange}
          style={styles.input}
        >
          {EMPLOYMENT_TYPES.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      <div style={styles.fieldRow}>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>Expected Salary</label>
          <input
            type="number"
            name="salaryAmount"
            value={formData.salaryAmount}
            onChange={onChange}
            placeholder="0"
            style={styles.input}
          />
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>Currency</label>
          <select
            name="salaryCurrency"
            value={formData.salaryCurrency}
            onChange={onChange}
            style={styles.input}
          >
            <option value="INR">INR (₹)</option>
            <option value="USD">USD ($)</option>
            <option value="EUR">EUR (€)</option>
          </select>
        </div>
      </div>

      <div style={styles.infoBox}>
        <AlertCircle size={16} style={{ marginRight: '8px' }} />
        <span>Your salary information is confidential and only visible to HR</span>
      </div>
    </div>
  );
}

function Step4({ formData, onFileUpload, onRemoveDocument, saving }) {
  return (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>Required Documents</h2>

      <p style={styles.fieldDescription}>Upload the following documents (PDF, JPG, PNG max 10MB each)</p>

      {DOCUMENT_TYPES.map(docType => (
        <DocumentUpload
          key={docType.value}
          docType={docType}
          documents={formData.documents}
          onUpload={onFileUpload}
          onRemove={onRemoveDocument}
          saving={saving}
        />
      ))}
    </div>
  );
}

function Step5({ formData, onChange }) {
  return (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>Banking & Emergency Contact</h2>

      <div style={styles.sectionTitle}>Emergency Contact</div>

      <div style={styles.fieldGroup}>
        <label style={styles.label}>Emergency Contact Name</label>
        <input
          type="text"
          name="emergencyContactName"
          value={formData.emergencyContactName}
          onChange={onChange}
          placeholder="Full name"
          style={styles.input}
        />
      </div>

      <div style={styles.fieldRow}>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>Phone</label>
          <input
            type="tel"
            name="emergencyContactPhone"
            value={formData.emergencyContactPhone}
            onChange={onChange}
            placeholder="+91 9876543210"
            style={styles.input}
          />
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>Relationship</label>
          <select
            name="emergencyContactRelation"
            value={formData.emergencyContactRelation}
            onChange={onChange}
            style={styles.input}
          >
            <option value="">Select</option>
            <option value="Spouse">Spouse</option>
            <option value="Parent">Parent</option>
            <option value="Sibling">Sibling</option>
            <option value="Friend">Friend</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      <div style={styles.sectionTitle}>Bank Account Information</div>

      <div style={styles.fieldGroup}>
        <label style={styles.label}>Account Holder Name</label>
        <input
          type="text"
          name="bankAccountName"
          value={formData.bankAccountName}
          onChange={onChange}
          placeholder="Name as per bank records"
          style={styles.input}
        />
      </div>

      <div style={styles.fieldRow}>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>Account Number</label>
          <input
            type="text"
            name="bankAccountNumber"
            value={formData.bankAccountNumber}
            onChange={onChange}
            placeholder="1234567890"
            style={styles.input}
          />
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>IFSC Code</label>
          <input
            type="text"
            name="bankIfscCode"
            value={formData.bankIfscCode}
            onChange={onChange}
            placeholder="SBIN0001234"
            style={styles.input}
          />
        </div>
      </div>

      <div style={styles.fieldGroup}>
        <label style={styles.label}>Bank Branch Name</label>
        <input
          type="text"
          name="bankBranchName"
          value={formData.bankBranchName}
          onChange={onChange}
          placeholder="Branch name"
          style={styles.input}
        />
      </div>

      <div style={styles.infoBox}>
        <AlertCircle size={16} style={{ marginRight: '8px' }} />
        <span>Your banking information is encrypted and used only for salary processing</span>
      </div>
    </div>
  );
}

function DocumentUpload({ docType, documents, onUpload, onRemove, saving }) {
  const uploaded = documents.find(d => d.document_type === docType.value);

  return (
    <div style={styles.documentItem}>
      <div style={styles.documentHeader}>
        <div style={styles.documentLabel}>
          <FileText size={18} style={{ marginRight: '10px', color: colors.primary }} />
          <span>{docType.label}</span>
        </div>
        {uploaded && <Check size={18} style={{ color: colors.success }} />}
      </div>

      {uploaded ? (
        <div style={styles.uploadedFile}>
          <span>{uploaded.file_name}</span>
          <button
            onClick={() => onRemove(uploaded.id)}
            style={styles.removeBtn}
            title="Remove"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <label style={styles.uploadLabel}>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            onChange={(e) => onUpload(e, docType.value)}
            style={{ display: 'none' }}
            disabled={saving}
          />
          <Upload size={20} style={{ marginRight: '8px' }} />
          <span>Click to upload or drag and drop</span>
        </label>
      )}
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: colors.bg,
    paddingTop: '40px',
    paddingBottom: '40px',
  },
  header: {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
    color: '#fff',
    padding: '40px 0',
    marginBottom: '40px',
  },
  headerContent: {
    maxWidth: '800px',
    margin: '0 auto',
    paddingX: '20px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    margin: '0 0 10px 0',
  },
  subtitle: {
    fontSize: '16px',
    opacity: 0.9,
    margin: 0,
  },
  mainContainer: {
    maxWidth: '800px',
    margin: '0 auto',
    paddingX: '20px',
  },
  stepsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '40px',
    gap: '20px',
    padding: '20px',
    backgroundColor: colors.cardBg,
    borderRadius: '8px',
  },
  stepWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    position: 'relative',
  },
  stepDot: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    marginBottom: '8px',
    transition: 'all 0.2s',
  },
  stepLabel: {
    fontSize: '12px',
    textAlign: 'center',
    color: colors.textLight,
  },
  stepLine: {
    position: 'absolute',
    height: '2px',
    width: '100%',
    top: '20px',
    left: '50%',
    zIndex: -1,
  },
  card: {
    backgroundColor: colors.cardBg,
    borderRadius: '8px',
    padding: '40px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    marginBottom: '20px',
  },
  stepContent: {
    minHeight: '300px',
  },
  stepTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: '30px',
  },
  fieldGroup: {
    marginBottom: '20px',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '8px',
    color: colors.text,
  },
  fieldDescription: {
    fontSize: '13px',
    color: colors.textLight,
    marginBottom: '20px',
    fontStyle: 'italic',
  },
  input: {
    width: '100%',
    padding: '12px',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  infoBox: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#e3f2fd',
    border: `1px solid #90caf9`,
    borderRadius: '6px',
    fontSize: '13px',
    color: '#1976d2',
    marginTop: '20px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: colors.text,
    marginTop: '30px',
    marginBottom: '20px',
    paddingBottom: '10px',
    borderBottom: `2px solid ${colors.primary}`,
  },
  documentItem: {
    padding: '16px',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    marginBottom: '12px',
    backgroundColor: '#fafafa',
  },
  documentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  documentLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: '500',
  },
  uploadLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    border: `2px dashed ${colors.primary}`,
    borderRadius: '6px',
    cursor: 'pointer',
    color: colors.primary,
    fontSize: '14px',
    transition: 'all 0.2s',
    backgroundColor: `${colors.primary}08`,
  },
  uploadedFile: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: colors.cardBg,
    borderRadius: '6px',
    fontSize: '13px',
    color: colors.success,
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.error,
    padding: '4px',
  },
  formFooter: {
    marginTop: '40px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: `1px solid ${colors.border}`,
    paddingTop: '20px',
  },
  saveStatus: {
    fontSize: '12px',
    minWidth: '100px',
  },
  navigationButtons: {
    display: 'flex',
    gap: '12px',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: colors.primary,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  buttonSecondary: {
    backgroundColor: colors.border,
    color: colors.text,
  },
  successCard: {
    textAlign: 'center',
    padding: '60px 40px',
    backgroundColor: colors.cardBg,
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  successIcon: {
    fontSize: '64px',
    color: colors.success,
    marginBottom: '20px',
  },
  successTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: '15px',
  },
  successText: {
    fontSize: '16px',
    color: colors.textLight,
    marginBottom: '10px',
    lineHeight: '1.6',
  },
  successSubtext: {
    fontSize: '14px',
    color: colors.textLight,
  },
  footerNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: colors.textLight,
    textAlign: 'center',
    justifyContent: 'center',
    marginTop: '20px',
  },
};
